/**
 * Email Reports API Client
 * 
 * Handles email report subscriptions for weekly planning reports.
 * Uses the same auth pattern as lib/apiAccounts.ts but hits a different endpoint prefix.
 * 
 * Backend structure:
 * - Accounts: /api/v1/accounts/... (me, favorites, views)
 * - Email:    /api/v1/email/...    (reports, subscribe)
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import { AccountsApiError, getEmailFromIdToken } from './apiAccounts';
import { getOrCreateAnalyticsClientId } from './analyticsIdentity';

// ─────────────────────────────────────────────────────────────────────────────
// Internal fetch helper for email endpoints
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get the base URL for email endpoints.
 * 
 * NEXT_PUBLIC_API_URL is typically set to the accounts prefix:
 *   https://api.city-scope.co.uk/api/v1/accounts
 * 
 * But email endpoints are at:
 *   https://api.city-scope.co.uk/api/v1/email/...
 * 
 * So we strip /v1/accounts (or similar) and use /v1/email instead.
 */
function getEmailApiBaseUrl(): string {
  // Route browser calls through the Next API proxy so the frontend does not
  // depend on the upstream email API exposing CORS headers.
  if (typeof window !== 'undefined') {
    return '/api';
  }

  // Backend email endpoints are at: https://api.city-scope.co.uk/api/v1/email/...
  const rawBaseUrl = process.env.NEXT_PUBLIC_API_URL;

  if (!rawBaseUrl) {
    throw new AccountsApiError('Missing NEXT_PUBLIC_API_URL', 0);
  }

  const trimmed = rawBaseUrl.trim();
  if (trimmed.startsWith('/')) {
    // Support same-origin proxying configs like NEXT_PUBLIC_API_URL=/api/v1/accounts
    // by targeting the site's own /api/v1/email/... endpoints.
    return '/api';
  }

  // Extract just the origin (protocol + host) and always append /api
  try {
    const url = new URL(trimmed);
    return url.origin + '/api';
  } catch {
    // Fallback: strip everything after the domain and add /api
    const origin = trimmed.replace(/^(https?:\/\/[^/]+).*$/, '$1');
    return origin + '/api';
  }
}

/**
 * Fetch helper specifically for email endpoints.
 * Handles auth token injection and 401 retry like apiFetchAccounts.
 */
async function apiFetchEmail<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const baseUrl = getEmailApiBaseUrl();

  const execute = async (sessionOverride?: Awaited<ReturnType<typeof fetchAuthSession>>): Promise<Response> => {
    let activeSession = sessionOverride;
    if (!activeSession) {
      try {
        activeSession = await fetchAuthSession();
      } catch {
        throw new AccountsApiError('Authentication session unavailable', 401);
      }
    }

    // Prefer ID tokens for API authorization.
    // The current backend JWT verifier expects an `aud` claim, which Cognito ID tokens have.
    // (Cognito access tokens often omit `aud` and use `client_id` instead.)
    const idToken = activeSession.tokens?.idToken?.toString();
    const accessToken = activeSession.tokens?.accessToken?.toString();
    const candidates = [idToken, accessToken].filter((t, i, arr) => Boolean(t) && arr.indexOf(t) === i) as string[];
    if (candidates.length === 0) throw new AccountsApiError('Not authenticated', 401);

    const headers = new Headers(init.headers as HeadersInit | undefined);

    const hasBody = init.body !== undefined && init.body !== null;
    if (hasBody && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    try {
      let lastRes: Response | null = null;
      for (const token of candidates) {
        headers.set('Authorization', 'Bearer ' + token);
        lastRes = await fetch(baseUrl + path, { ...init, headers });
        if (lastRes.status !== 401) return lastRes;
      }
      return lastRes as Response;
    } catch {
      throw new AccountsApiError('Network error', 0);
    }
  };

  let res = await execute();

  // Retry on 401 with refreshed session
  if (res.status === 401) {
    try {
      const refreshedSession = await fetchAuthSession({ forceRefresh: true } as Parameters<typeof fetchAuthSession>[0]);
      res = await execute(refreshedSession);
    } catch {
      throw new AccountsApiError('Authentication session unavailable', 401);
    }
  }

  return parseEmailApiResponse<T>(res);
}

async function apiFetchPublicEmail<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const baseUrl = getEmailApiBaseUrl();
  const headers = new Headers(init.headers as HeadersInit | undefined);

  const hasBody = init.body !== undefined && init.body !== null;
  if (hasBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  let res: Response;
  try {
    res = await fetch(baseUrl + path, { ...init, headers });
  } catch {
    throw new AccountsApiError('Network error', 0);
  }

  return parseEmailApiResponse<T>(res);
}

function formatEmailApiDetail(detail: unknown): string | undefined {
  if (typeof detail === 'string') {
    return detail;
  }
  if (!Array.isArray(detail)) {
    return undefined;
  }

  const messages = detail
    .map((item) => {
      if (typeof item === 'string') return item;
      if (!item || typeof item !== 'object') return null;
      const record = item as { msg?: unknown; loc?: unknown };
      const msg = typeof record.msg === 'string' ? record.msg : null;
      if (!msg) return null;
      if (!Array.isArray(record.loc) || record.loc.length === 0) return msg;
      const field = record.loc
        .filter((part) => typeof part === 'string' || typeof part === 'number')
        .map(String)
        .join('.');
      return field ? `${field}: ${msg}` : msg;
    })
    .filter((value): value is string => Boolean(value));

  return messages.length > 0 ? messages.join(' ') : undefined;
}

async function parseEmailApiResponse<T>(res: Response): Promise<T> {
  const contentType = res.headers.get('content-type')?.toLowerCase() ?? '';
  const text = await res.text();
  const trimmed = text.trim();

  if (!res.ok) {
    let parsed: unknown = undefined;
    if (trimmed && (contentType.includes('json') || trimmed.startsWith('{') || trimmed.startsWith('['))) {
      try { parsed = JSON.parse(trimmed); } catch { /* noop */ }
    }
    const parsedRecord =
      parsed && typeof parsed === 'object'
        ? (parsed as { detail?: unknown })
        : null;
    const detail = formatEmailApiDetail(parsedRecord?.detail);
    throw new AccountsApiError(detail ? `email API ${res.status}: ${detail}` : `email API ${res.status}`, res.status, parsed);
  }

  if (trimmed.length === 0) return undefined as T;

  if (contentType.includes('json') || (!contentType && (trimmed.startsWith('{') || trimmed.startsWith('[')))) {
    try { return JSON.parse(trimmed) as T; }
    catch { throw new AccountsApiError('Invalid JSON response from email API', res.status); }
  }

  return trimmed as unknown as T;
}

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface EmailReportPublic {
  report_key: string;        // e.g. "weekly_edinburgh"
  title: string;             // e.g. "Weekly Planning Report (Edinburgh)"
  cadence: 'daily' | 'weekly' | 'monthly';
  timezone: string;          // e.g. "Europe/London"
  send_hour: number;         // 0–23
  send_minute: number;       // 0–59
  send_dow?: number | null;  // 0–6, only for weekly (Mon=1 etc)
  is_active: boolean;
  scope?: 'city' | 'community';
  community_slug?: string;
  community_name?: string;
}

export type SubscribeStatus = 'subscribed' | 'already_subscribed' | 'resubscribed';

export interface SubscribeResponse {
  ok: true;
  status: SubscribeStatus;
  /** Unsubscribe token (if returned by backend) for use in unsubscribe requests */
  unsubscribe_token?: string;
}

export interface UnsubscribeResponse {
  ok: boolean;
}

export interface UserSubscription {
  report_key: string;
  status: 'active' | 'unsubscribed' | 'bounced' | 'complained';
  unsubscribe_token: string;
  created_at?: string;
  last_sent_at?: string | null;
}

export interface SubscribeOptions {
  /** Override the email address (defaults to Cognito user's email) */
  emailOverride?: string;
  /** Analytics source identifier */
  source?: string;
  /** Stable anonymous analytics client id used to attach signup attribution to the current Matomo visit */
  analyticsClientId?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// API Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * GET /api/v1/email/subscriptions
 * 
 * Fetches the current user's email subscriptions.
 * Requires authentication.
 */
export async function fetchUserSubscriptions(): Promise<UserSubscription[]> {
  try {
    const subscriptions = await apiFetchEmail<UserSubscription[]>('/v1/email/subscriptions', {
      method: 'GET',
    });
    return subscriptions ?? [];
  } catch (error) {
    if (error instanceof AccountsApiError) {
      // 404 means the endpoint isn't deployed yet - return empty array
      if (error.status === 404) {
        console.debug('Email subscriptions endpoint not available (404)');
        return [];
      }
      // 401 means user isn't authenticated - return empty silently
      if (error.status === 401) {
        console.debug('Not authenticated for email subscriptions');
        return [];
      }
      throw new Error(`Failed to fetch subscriptions: ${error.message}`);
    }
    throw new Error('Failed to fetch subscriptions: Network error');
  }
}

/**
 * GET /api/v1/email/reports
 * 
 * Fetches the list of available email reports from the backend.
 */
export async function fetchEmailReports(): Promise<EmailReportPublic[]> {
  try {
    const reports = await apiFetchPublicEmail<EmailReportPublic[]>('/v1/email/reports', {
      method: 'GET',
    });
    return reports ?? [];
  } catch (error) {
    if (error instanceof AccountsApiError) {
      // 404 means the email reports endpoint isn't deployed yet - return empty array
      if (error.status === 404) {
        console.debug('Email reports endpoint not available (404) - feature may not be deployed yet');
        return [];
      }
      // 401 means user isn't authenticated - return empty silently
      if (error.status === 401) {
        console.debug('Not authenticated for email reports');
        return [];
      }
      throw new Error(`Failed to fetch email reports: ${error.message}`);
    }
    throw new Error('Failed to fetch email reports: Network error');
  }
}

/**
 * POST /api/v1/email/subscribe
 * 
 * Subscribes the current logged-in user to an email report.
 * Uses the Cognito user's email by default.
 * 
 * @param reportKey - The report key to subscribe to (e.g. "weekly_edinburgh")
 * @param options - Optional settings including email override and source
 */
export async function subscribeToEmailReport(
  reportKey: string,
  options: SubscribeOptions = {}
): Promise<SubscribeResponse> {
  const { emailOverride, source = 'accounts_page' } = options;
  const analyticsClientId = options.analyticsClientId ?? getOrCreateAnalyticsClientId() ?? undefined;

  // Get email from the current user's session
  let email = emailOverride;
  if (!email) {
    const me = await getEmailFromIdToken();
    if (!me?.email) {
      throw new Error('Unable to determine user email. Please sign in again.');
    }
    email = me.email;
  }

  if (!email) {
    throw new Error('Email address is required to subscribe');
  }

  try {
    const response = await apiFetchPublicEmail<SubscribeResponse>('/v1/email/subscribe', {
      method: 'POST',
      body: JSON.stringify({
        email,
        report_key: reportKey,
        source,
        ...(analyticsClientId ? { analytics_client_id: analyticsClientId } : {}),
      }),
    });

    if (!response?.ok) {
      throw new Error('Subscribe request did not return a successful response');
    }

    return response;
  } catch (error) {
    if (error instanceof AccountsApiError) {
      // 404 means the endpoint isn't deployed yet
      if (error.status === 404) {
        throw new Error('Email subscriptions are not available yet. Please try again later.');
      }
      // Try to extract a meaningful error message from the response body
      const body = error.body as Record<string, unknown> | undefined;
      const detail = body?.detail;
      if (typeof detail === 'string') {
        throw new Error(detail);
      }
      throw new Error(`Failed to subscribe: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to subscribe: Network error');
  }
}

/**
 * POST /api/v1/email/unsubscribe
 *
 * Unsubscribes using a token (no auth required).
 * This is used both for email unsubscribe links and in-app unsubscribe buttons.
 *
 * @param token - The unsubscribe token from the email or subscribe response
 */
export async function unsubscribeWithToken(token: string): Promise<UnsubscribeResponse> {
  if (!token) {
    throw new Error('Unsubscribe token is required');
  }

  const baseUrl = getEmailApiBaseUrl();
  const fullUrl = baseUrl + '/v1/email/unsubscribe';

  const res = await fetch(fullUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  if (!res.ok) {
    if (res.status === 404) {
      throw new Error('This unsubscribe link is invalid or has already been used.');
    }
    // Try to get error detail from response
    try {
      const data = await res.json();
      if (typeof data?.detail === 'string') {
        throw new Error(data.detail);
      }
    } catch { /* ignore parse errors */ }
    throw new Error(`Unsubscribe failed (${res.status})`);
  }

  // Success - return ok response
  return { ok: true };
}

// ─────────────────────────────────────────────────────────────────────────────
// Utility Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Format the schedule description for a report
 * e.g. "Weekly · Monday 07:00 · Europe/London"
 */
export function formatReportSchedule(report: EmailReportPublic): string {
  const parts: string[] = [];

  // Cadence
  const cadenceLabel = report.cadence.charAt(0).toUpperCase() + report.cadence.slice(1);
  parts.push(cadenceLabel);

  // Day of week for weekly reports
  if (report.cadence === 'weekly' && report.send_dow != null) {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    parts.push(days[report.send_dow] || '');
  }

  // Time
  const hour = report.send_hour.toString().padStart(2, '0');
  const minute = report.send_minute.toString().padStart(2, '0');
  parts.push(`${hour}:${minute}`);

  // Timezone (simplified display)
  const tzShort = report.timezone.split('/').pop() || report.timezone;
  parts.push(tzShort);

  return parts.filter(Boolean).join(' · ');
}

/**
 * Get a user-friendly message for a subscription status
 */
export function getSubscribeStatusMessage(status: SubscribeStatus): string {
  switch (status) {
    case 'subscribed':
      return "You're now subscribed! Check your inbox for a confirmation.";
    case 'already_subscribed':
      return "You're already subscribed to this report.";
    case 'resubscribed':
      return 'Subscription reactivated! Welcome back.';
    default:
      return 'Subscription successful!';
  }
}
